import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { TextField } from '@mui/material';
import {
    makeStyles,
    useTheme,
} from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApButton,
    ApDropdown,
    ApDropdownItem,
    ApIconButton,
    ApTextField,
    ApTooltip,
} from '@uipath/portal-shell-react';
import type { ApDropdownCustomEvent } from '@uipath/portal-shell-types';
import React from 'react';
import {
    Controller,
    useWatch,
} from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useBreadcrumbs } from '../../common/providers/BreadcrumbProvider';
import UiForm from '../common/UiForm';
import UiPageContainer from '../common/UiPageContainer/UiPageContainer';
import AdminBreadCrumbs from '../organizationsettings/AdminBreadCrumbs';
import useRegisterRelayGroupViewModel from './RegisterRelayGroupViewModel';
import {
    isValidEndpointHost,
    parseEndpointUrl,
} from './relayEndpointUtil';

const ENDPOINT_SCHEME_PREFIX_REGEX = /^(https?|tcp):\/\//i;

const ENDPOINT_SCHEME_OPTIONS = [
    {
        value: 'https',
        label: 'HTTPS',
    },
    {
        value: 'http',
        label: 'HTTP',
    },
    {
        value: 'tcp',
        label: 'TCP',
    },
] as const;

const useStyles = makeStyles((theme) =>
    createStyles({
        endpointContainer: {
            margin: theme.spacing(2.5, 0, 2.5, 0),
            display: 'flex',
            flexDirection: 'column',
        },
        endpointFieldsContainer: {
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '4px',
            margin: theme.spacing(0, 0.5, 1.25, 0.5),
        },
        addButton: {
            alignSelf: 'flex-start',
            padding: theme.spacing(0, 0.5, 1.25, 0.5),
        },
        deleteButtonWithLabel: { marginTop: theme.spacing(5) },
        deleteButtonWithoutLabel: { marginTop: theme.spacing(0.5) },
        restartNotice: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            backgroundColor: theme.palette.info.light,
            borderRadius: theme.shape.borderRadius,
            marginTop: '16px',
        },
        restartNoticeIcon: {
            fontSize: '20px',
            color: theme.palette.info.main,
            flexShrink: 0,
        },
        tooltipIcon: {
            width: theme.spacing(2),
            height: theme.spacing(2),
            fontWeight: '600',
        },
        iconButton: {
            padding: '0',
            marginLeft: theme.spacing(-0.25),
            minWidth: 'auto',
            height: 'auto',
            alignSelf: 'center',
        },
    })
);

interface TooltipProps {
    textKey: string;
    placement?: 'top' | 'right' | 'bottom' | 'left' | 'top-start';
}

interface InfoTooltipProps{
    label: string;
    required?: boolean;
    tooltip: TooltipProps;
    dataCy?: string;
}

const InfoLabelTooltip: React.FC<InfoTooltipProps> = ({
    label,
    required = false,
    tooltip,
    dataCy,
}) => {
    const { formatMessage: translate } = useIntl();
    const theme = useTheme();
    const classes = useStyles();

    const tooltipText = translate({ id: tooltip.textKey });

    return (
        <UiStack
            direction='row'
            align='center'
            mb={SpacingToken.Micro}
            data-cy={dataCy}
        >
            <UiText
                variant={FontVariantToken.fontSizeMBold}
                color={theme.palette.semantic.colorForegroundDeEmp}
            >
                {label}
                {required && ' *'}
            </UiText>
            <ApTooltip
                placement={tooltip.placement}
                content={tooltipText}
            >
                <ApIconButton
                    className={classes.iconButton}
                    data-cy={dataCy ? `${dataCy}-icon` : undefined}>
                    <InfoOutlinedIcon
                        color='action'
                        fontSize='small'
                        className={classes.tooltipIcon}
                        aria-label={tooltipText}
                    />
                </ApIconButton>
            </ApTooltip>
        </UiStack>
    );
};

const RegisterRelayGroup: React.FC<{ type: 'add' | 'edit' }> = ({ type }) => {
    const { formatMessage: translate } = useIntl();
    const { breadcrumbs } = useBreadcrumbs();
    const classes = useStyles();

    const {
        loading,
        methods: {
            formState: {
                errors, isSubmitting,
            },
            handleSubmit,
            control,
            setValue,
            onCancel,
        },
        fieldArray: {
            fields,
            append,
            remove,
        },
    } = useRegisterRelayGroupViewModel({ type });

    const EnableRelayTCP = useFeatureFlagValue(Features.EnableRelayTCP.name);

    const endpointValues = useWatch({
        control,
        name: 'endpoints',
    });

    const isTcpEndpoint = (index: number) => endpointValues?.[index]?.scheme === 'tcp';

    const getHostPlaceholder = (index: number) => translate({
        id: isTcpEndpoint(index)
            ? 'CLIENT_RELAY_ENDPOINT_TCP_HOST_PLACEHOLDER'
            : 'CLIENT_RELAY_ENDPOINT_HOST_PLACEHOLDER',
    });

    const handleHostChange = (index: number, value: string | undefined) => {
        const trimmedValue = value?.trim() ?? '';
        const parsed = parseEndpointUrl(trimmedValue);
        if (ENDPOINT_SCHEME_PREFIX_REGEX.test(trimmedValue)) {
            setValue(`endpoints.${index}.scheme`, parsed.scheme, { shouldDirty: true });
        }
        setValue(`endpoints.${index}.host`, parsed.host, {
            shouldDirty: true,
            shouldValidate: true,
        });
    };

    return (
        <UiPageContainer
            loading={loading}
            maxWidth='800px'
            breadcrumb={<AdminBreadCrumbs breadCrumbTrail={breadcrumbs} />}>
            <UiForm
                onSubmit={handleSubmit}
                actions={
                    <UiStack
                        direction='row'
                        justify='end'
                        gap={SpacingToken.S}>
                        <ApButton
                            onClick={onCancel}
                            variant="secondary"
                            data-cy="cancel-register-relay-button"
                            label={translate({ id: 'CLIENT_CANCEL' })} />
                        <ApButton
                            type="submit"
                            variant="primary"
                            loading={isSubmitting}
                            data-cy="confirm-register-relay-button"
                            label={translate({ id: type === 'add' ? 'CLIENT_SAVE' : 'CLIENT_UPDATE' })} />
                    </UiStack>
                }>
                <Controller
                    name="name"
                    control={control}
                    rules={{
                        required: translate(
                            { id: 'CLIENT_REQUIRED_FIELD_ERROR_SPECIFIC' },
                            { 0: translate({ id: 'CLIENT_NAME' }) },
                        ),
                        minLength: 1,
                        maxLength: {
                            value: 100,
                            message: translate({ id: 'CLIENT_MAX_LENGTH_ERROR_SPECIFIC' }, {
                                field: translate({ id: 'CLIENT_NAME' }),
                                num: 100,
                            }),
                        },
                    }}
                    render={({ field }) => (
                        <ApTextField
                            required
                            disabled={type === 'edit' || undefined}
                            label={translate({ id: 'CLIENT_NAME' })}
                            placeholder={translate({ id: 'CLIENT_ENTER_RELAY_GROUP_NAME' })}
                            error={!!errors.name}
                            helperText={errors?.name?.message}
                            onValueChanged={e => field.onChange(e.detail)}
                            data-cy="relay-group-name"
                            {...field}
                        />
                    )} />
                <Controller
                    name="description"
                    control={control}
                    rules={{
                        maxLength: {
                            value: 500,
                            message: translate({ id: 'CLIENT_DESCRIPTION_MAX_ERROR' }, { charLimit: 500 }),
                        },
                    }}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            placeholder={translate({ id: 'CLIENT_ENTER_RELAY_GROUP_DESCRIPTION' })}
                            multiline
                            rows={3}
                            error={!!errors.description}
                            helperText={errors?.description?.message}
                            variant="outlined"
                            label={translate({ id: 'CLIENT_RELAY_GROUP_DESCRIPTION' })}
                            fullWidth
                            data-cy="relay-group-description"
                            inputProps={{ 'aria-label': translate({ id: 'CLIENT_DESCRIPTION' }) }}
                        />
                    )} />

                <div className={classes.endpointContainer}>
                    <UiText variant={FontVariantToken.fontSizeMBold}>
                        {translate({ id: 'CLIENT_ENDPOINT_URLS' })}
                    </UiText>

                    {fields.map((field, index) => (
                        <div
                            key={field.id}
                            className={classes.endpointFieldsContainer}>
                            <UiStack
                                direction="row"
                                gap={SpacingToken.S}
                                align="start">
                                <Controller
                                    name={`endpoints.${index}.name` as const}
                                    control={control}
                                    rules={{
                                        required: translate(
                                            { id: 'CLIENT_REQUIRED_FIELD_ERROR_SPECIFIC' },
                                            { 0: translate({ id: 'CLIENT_NAME' }) },
                                        ),
                                        pattern: {
                                            value: /^\w(?:[\w .-]*\w)?$/,
                                            message: translate({ id: 'CLIENT_INVALID_NAME_ERROR' }),
                                        },
                                    }}
                                    render={({ field: nameField }) => (
                                        <ApTextField
                                            required
                                            style={{ flex: 1 }}
                                            label={index === 0 ? translate({ id: 'CLIENT_NAME' }) : undefined}
                                            placeholder={translate({ id: 'CLIENT_NAME' })}
                                            error={!!errors.endpoints?.[index]?.name}
                                            helperText={errors.endpoints?.[index]?.name?.message}
                                            onValueChanged={e => nameField.onChange(e.detail)}
                                            data-cy={`endpoint-${index}-name`}
                                            {...nameField}
                                        />
                                    )} />

                                {EnableRelayTCP ? (
                                    <UiStack
                                        direction='column'
                                        style={{ flex: 2 }}>
                                        {index === 0 && <InfoLabelTooltip
                                            label={translate({ id: 'CLIENT_URL' })}
                                            required
                                            tooltip={{
                                                textKey: 'CLIENT_RELAY_ENDPOINT_URL_TOOLTIP',
                                                placement: 'right',
                                            }}
                                            dataCy={`endpoint-${index}-url-label`}
                                        />}
                                        <UiStack
                                            direction='row'
                                            gap={SpacingToken.XS}
                                            align='start'>
                                            <Controller
                                                name={`endpoints.${index}.scheme` as const}
                                                control={control}
                                                render={({ field: schemeField }) => (
                                                    <ApDropdown
                                                        style={{ width: '120px' }}
                                                        disabled={(type === 'edit' && field.isExisting) || undefined}
                                                        selectedValue={schemeField.value}
                                                        onSelectedValueChanged={(e: ApDropdownCustomEvent<string | string[]>) =>
                                                            schemeField.onChange(Array.isArray(e.detail) ? e.detail[0] : e.detail)}
                                                        data-cy={`endpoint-${index}-scheme`}>
                                                        {ENDPOINT_SCHEME_OPTIONS.map((option) => (
                                                            <ApDropdownItem
                                                                key={option.value}
                                                                value={option.value}
                                                                label={option.label} />
                                                        ))}
                                                    </ApDropdown>
                                                )} />
                                            <Controller
                                                name={`endpoints.${index}.host` as const}
                                                control={control}
                                                rules={{
                                                    required: translate(
                                                        { id: 'CLIENT_REQUIRED_FIELD_ERROR_SPECIFIC' },
                                                        { 0: translate({ id: 'CLIENT_URL' }) },
                                                    ),
                                                    validate: (value) =>
                                                        isValidEndpointHost(value)
                                                        || translate({ id: 'CLIENT_INVALID_RELAY_ENDPOINT_HOST_ERROR' }),
                                                }}
                                                render={({ field: hostField }) => (
                                                    <ApTextField
                                                        style={{ flex: 1 }}
                                                        disabled={(type === 'edit' && field.isExisting) || undefined}
                                                        placeholder={getHostPlaceholder(index)}
                                                        error={!!errors.endpoints?.[index]?.host}
                                                        helperText={errors.endpoints?.[index]?.host?.message}
                                                        onValueChanged={e => handleHostChange(index, e.detail)}
                                                        data-cy={`endpoint-${index}-host`}
                                                        {...hostField}
                                                    />
                                                )} />
                                        </UiStack>
                                    </UiStack>
                                ) : (
                                    <Controller
                                        name={`endpoints.${index}.url` as const}
                                        control={control}
                                        rules={{
                                            required: translate(
                                                { id: 'CLIENT_REQUIRED_FIELD_ERROR_SPECIFIC' },
                                                { 0: translate({ id: 'CLIENT_URL' }) },
                                            ),
                                            pattern: {
                                                value: /^https?:\/\/.+$/i,
                                                message: translate({ id: 'CLIENT_INVALID_URL_ERROR' }),
                                            },
                                        }}
                                        render={({ field: urlField }) => (
                                            <UiStack
                                                direction='column'
                                                style={{ flex: 2 }}>
                                                {index === 0 && <InfoLabelTooltip
                                                    label={translate({ id: 'CLIENT_URL' })}
                                                    required
                                                    tooltip={{
                                                        textKey: 'CLIENT_RELAY_ENDPOINT_URL_TOOLTIP',
                                                        placement: 'right',
                                                    }}
                                                    dataCy={`endpoint-${index}-url-label`}
                                                />}
                                                <ApTextField
                                                    disabled={(type === 'edit' && field.isExisting) || undefined}
                                                    placeholder={translate({ id: 'CLIENT_RELAY_ENDPOINT_URL_PLACEHOLDER' })}
                                                    error={!!errors.endpoints?.[index]?.url}
                                                    helperText={errors.endpoints?.[index]?.url?.message}
                                                    onValueChanged={e => urlField.onChange(e.detail?.trim() ?? '')}
                                                    data-cy={`endpoint-${index}-url`}
                                                    {...urlField}
                                                />
                                            </UiStack>
                                        )} />
                                )}

                                <Controller
                                    name={`endpoints.${index}.healthCheckPath` as const}
                                    control={control}
                                    rules={{
                                        validate: (value) =>
                                            isTcpEndpoint(index)
                                            || !value
                                            || value.startsWith('/')
                                            || translate({ id: 'CLIENT_RELAY_HEALTH_CHECK_PATH_INVALID' }),
                                        maxLength: 500,
                                    }}
                                    render={({ field: healthCheckPathField }) => (
                                        <UiStack
                                            direction='column'
                                            style={{ flex: 1 }}
                                        >
                                            {index === 0 && <InfoLabelTooltip
                                                label={translate({ id: 'CLIENT_RELAY_HEALTH_CHECK_PATH' })}
                                                tooltip={{
                                                    textKey: EnableRelayTCP
                                                        ? 'CLIENT_RELAY_HEALTH_CHECK_PATH_TOOLTIP_TCP'
                                                        : 'CLIENT_RELAY_HEALTH_CHECK_PATH_TOOLTIP',
                                                    placement: 'right',
                                                }}
                                                dataCy={`endpoint-${index}-health-check-path-label`}
                                            />}
                                            <ApTextField
                                                disabled={isTcpEndpoint(index) || undefined}
                                                placeholder={translate({ id: 'CLIENT_RELAY_HEALTH_CHECK_PATH_PLACEHOLDER' })}
                                                helperText={errors.endpoints?.[index]?.healthCheckPath?.message}
                                                error={!isTcpEndpoint(index) && !!errors.endpoints?.[index]?.healthCheckPath}
                                                onValueChanged={e => healthCheckPathField.onChange(e.detail)}
                                                data-cy={`endpoint-${index}-health-check-path`}
                                                {...healthCheckPathField}
                                            />
                                        </UiStack>
                                    )} />
                                <ApIconButton
                                    className={index === 0 ? classes.deleteButtonWithLabel : classes.deleteButtonWithoutLabel}
                                    disabled={fields.length <= 1}
                                    onClick={() => remove(index)}
                                    data-cy={`delete-endpoint-${index}-button`}
                                    aria-label={translate({ id: 'CLIENT_DELETE_ENDPOINT' })}
                                >
                                    <DeleteOutlinedIcon />
                                </ApIconButton>
                            </UiStack>
                        </div>
                    ))}
                    <div className={classes.addButton}>
                        <ApButton
                            variant="secondary"
                            label={translate({ id: 'CLIENT_ADD' })}
                            onClick={() => append({
                                name: '',
                                description: '',
                                url: '',
                                scheme: 'https',
                                host: '',
                                healthCheckPath: '',
                            })}
                            data-cy="add-endpoint-button"
                        />
                    </div>
                </div>
            </UiForm>
        </UiPageContainer>
    );
};

export default RegisterRelayGroup;
